import type {
  FootprintReasonCode,
  GetFieldValidationResponse,
  MatchLevel,
} from '@onefootprint/request-types/dashboard';
import type { SignalShortInfoType, TransformedMatchSignalDataType } from './transform-response';

type FieldAttribute = keyof GetFieldValidationResponse;

const LEVEL_RANK: Record<MatchLevel, number> = { no_match: 0, could_not_match: 1, partial: 2, exact: 3 };

const FIELD_ORDER = [
  'name',
  'dob',
  'ssn',
  'address',
  'phone',
  'email',
  'document',
  'businessName',
  'businessDba',
  'businessTin',
  'businessAddress',
  'businessPhoneNumber',
  'businessBeneficialOwners',
] satisfies FieldAttribute[];

// Each of these only restates a field's overall result, which its card already shows in the title strip.
const SUMMARY_REASON_CODES = {
  name: ['name_matches', 'name_partially_matches', 'name_does_not_match'],
  dob: ['dob_matches', 'dob_partial_match', 'dob_does_not_match', 'dob_could_not_match'],
  ssn: ['ssn_matches', 'ssn_partially_matches', 'ssn_does_not_match'],
  address: ['address_matches', 'address_partially_matches', 'address_does_not_match'],
  phone: ['phone_located_matches', 'phone_located_partially_matches', 'phone_located_does_not_match'],
  businessName: ['business_name_match', 'business_name_could_not_match', 'business_name_does_not_match'],
  businessDba: ['business_dba_match', 'business_dba_does_not_match'],
  businessAddress: ['business_address_match', 'business_address_could_not_match', 'business_address_does_not_match'],
  businessPhoneNumber: ['business_phone_number_match', 'business_phone_number_does_not_match'],
  businessTin: ['tin_match', 'tin_does_not_match'],
} satisfies Partial<Record<FieldAttribute, FootprintReasonCode[]>>;

const isSummary = (attribute: string, matchLevel: MatchLevel, signal: SignalShortInfoType) => {
  const codesByAttribute: Partial<Record<string, readonly string[]>> = SUMMARY_REASON_CODES;
  return signal.matchLevel === matchLevel && (codesByAttribute[attribute]?.includes(signal.reasonCode) ?? false);
};

const getFieldRank = (attribute: string) => {
  const order: readonly string[] = FIELD_ORDER;
  const index = order.indexOf(attribute);
  return index === -1 ? order.length : index;
};

// A field without reasons sorts after one whose reasons all matched.
const getWorstRank = (signals: SignalShortInfoType[]) =>
  Math.min(LEVEL_RANK.exact + 1, ...signals.map(({ matchLevel }) => LEVEL_RANK[matchLevel]));

const orderSignals = ({ attribute, matchLevel, signals }: TransformedMatchSignalDataType) => {
  const details = signals.filter(signal => !isSummary(attribute, matchLevel, signal));
  // A summary is the only thing worth saying when the vendor gave no detail behind it.
  const shown = details.length ? details : signals;
  return shown.toSorted((a, b) => LEVEL_RANK[a.matchLevel] - LEVEL_RANK[b.matchLevel]);
};

const orderValidations = (validations: TransformedMatchSignalDataType[]): TransformedMatchSignalDataType[] =>
  validations
    .map(validation => ({ ...validation, signals: orderSignals(validation) }))
    .toSorted(
      (a, b) =>
        LEVEL_RANK[a.matchLevel] - LEVEL_RANK[b.matchLevel] ||
        getWorstRank(a.signals) - getWorstRank(b.signals) ||
        getFieldRank(a.attribute) - getFieldRank(b.attribute),
    );

export default orderValidations;
